import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Use current origin for API calls to work in both preview and production
const API = `${window.location.origin}/api`;

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspace, setWorkspace] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWorkspace = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/workspace`, { withCredentials: true });
      setWorkspace(response.data);
      setUser(response.data.user || null);
      setError(null);
    } catch (err) {
      if (err.response?.status !== 401) {
        setError(err.response?.data?.detail || 'Failed to fetch workspace');
      }
      setWorkspace(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/credits`, { withCredentials: true });
      setWorkspace(prev => prev ? { ...prev, credits: response.data.credits } : null);
      return response.data;
    } catch (err) {
      console.error('Failed to refresh credits:', err);
      return null;
    }
  }, []);

  const checkCredits = useCallback(async (action) => {
    try {
      const response = await axios.get(`${API}/credits/check/${action}`, { withCredentials: true });
      return response.data;
    } catch (err) {
      console.error('Failed to check credits:', err);
      return { has_enough: false, cost: 0, current_credits: 0 };
    }
  }, []);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  const value = {
    workspace,
    user,
    loading,
    error,
    fetchWorkspace,
    refreshCredits,
    checkCredits,
    isOwner: workspace?.current_user_role === 'owner',
    isEditor: ['owner', 'editor'].includes(workspace?.current_user_role),
    credits: workspace?.credits || 0
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
